import type { GetAppConfigResponse } from '@mc/autolyse/mailchimp.appshell.v1/AppConfigService';
import type { GetPageDataResponse } from '@mc/autolyse/mailchimp.appshell.v1/PageDataService';
import type { GetUserDataResponse } from '@mc/autolyse/mailchimp.appshell.v1/UserDataService';
import type { GetInternalToolsResponse } from '@mc/autolyse/mailchimp.appshell.v1/InternalToolsService';
import type { GetFlagsResponse } from '@mc/autolyse/mailchimp.appshell.v1/FlagsService';
import type { GetAnalyticsDataResponse } from '@mc/autolyse/mailchimp.appshell/AnalyticsService';

const internalToolsZeroValues: GetInternalToolsResponse = {
  experiments: {
    dummy_experiment: {
      // @ts-expect-error Front End expects a null value not an empty string
      current_variant: null,
      variants: [],
    },
  },
  flagUrls: {
    fetch: '',
    add: '',
    remove: '',
  },
  flags: [
    {
      enabled: '0',
      isOn: false,
      name: '',
      scheme: '',
    },
  ],
  intuitCompanyId: '',
  intuitProvisionalLinkEnv: '',
  intuitPseudonymId: '',
  isInternal: false,
  loginId: '',
  role: '',
  sesame: '',
  signedInViaMcAdmin: false,
  userExperimental: false,
  userId: 0,
  avestaEnvironment: '',
  msaasEnv: '',
  appVersion: '',
  mediaBuildVersion: '',
  xapiEnv: '',
  idxEnvironment: '',
};

const userDataZeroValues: GetUserDataResponse = {
  activeDate: 0,
  // @ts-expect-error ID zero values = null
  id: null,
  // @ts-expect-error ID zero values = null
  loginId: null,
  intuitPseudonymId: '',
  accountName: '',
  displayCurrency: '',
  displayCurrencySymbol: '',
  canSend: false,
  email: '',
  contactEmail: '',
  phone: '',
  avatarUrl: '',
  firstName: '',
  lastName: '',
  type: '',
  role: '',
  langPreference: '',
  genAIAllowedLanguages: [],
  languageName: '',
  langPage: '',
  countryCode: '',
  freeAudienceLimit: '',
  isPro: false,
  hasMarketingPlan: false,
  hasLegacyPlan: false,
  isPayGo: false,
  isCommercePlanFree: false,
  isPaused: false,
  openComplianceIssue: '',
  notifications: {
    count: 0,
    items: [],
  },
  hasPremiumMonthlyPlan: false,
  hasStandardMonthlyPlan: false,
  hasEssentialsMonthlyPlan: false,
  hasAnnualPlan: false,
  hasZendeskTalk: false,
  isUserLessThanThirtyOneDaysOld: false,
  isUserOnFreePlanForMoreThan30Days: false,
  isFree: false,
  isFreePlan: false,
  planAudienceSize: 0,
  planDisplayName: '',
  planTranslatedDisplayName: '',
  // @ts-expect-error ID zero values = null
  planId: null,
  // @ts-expect-error ID zero values = null
  invalidDowngradePlanId: null,
  downgradeStatus: {
    downgradeAuthorizedPayment: false,
    downgradeAuthorizedFree: false,
  },
  policy: {
    // @ts-expect-error Default Policy Value from free_monthly_plan_v0.yaml
    seatPolicyFeature: {
      maxInstances: 1,
      categoryRestrictions: [
        {
          name: 'role',
          allowedValues: ['owner'],
        },
      ],
    },
    // @ts-expect-error Default Policy Value from free_monthly_plan_v0.yaml
    templatePolicyFeature: {
      categoryRestrictions: [
        {
          name: 'category',
          allowedValues: ['basic_layouts', 'basic_themes'],
        },
      ],
    },
    // @ts-expect-error Default Policy Value from free_monthly_plan_v0.yaml
    audiencePolicyFeature: {
      maxInstances: 1,
    },
    // @ts-expect-error Default Policy Value from free_monthly_plan_v0.yaml
    regularSegmentationPolicyFeature: {
      categoryRestrictions: [
        {
          name: 'category',
          allowedValues: ['contact_event'],
        },
      ],
    },
    // @ts-expect-error Default Policy Value from free_monthly_plan_v0.yaml
    eventBasedSegmentationPolicyFeature: {
      tier: 1,
    },
    // @ts-expect-error Default Policy Value from free_monthly_plan_v0.yaml
    automationEventApiPolicyFeature: {
      maxInstances: 1,
    },
    // @ts-expect-error Default Policy Value from free_monthly_plan_v0.yaml
    automationPolicyFeature: {
      categoryRestrictions: [
        {
          name: 'workflow',
          allowedValues: [
            'abandonedBrowse',
            'abandonedCart',
            'categoryFollowup',
            'custom',
            'emailFollowup',
            'productFollowup',
            'purchaseFollowup',
            'reengageSupporters',
            'singleWelcome',
            'sitePopupThankYou',
            'tagAdd',
            'groupAddSingle',
            'groupRemoveSingle',
            'topSupportersSingle',
            'firstTimeCustomersSingle',
            'bestCustomersSingle',
            'reengageCustomersSingle',
            'apiSingle',
            'birthdaySingle',
            'dateAddedSingle',
            'recurringEventSingle',
            'specialEventSingle',
            'eventApi',
          ],
        },
      ],
    },
    // @ts-expect-error Default Policy Value from free_monthly_plan_v0.yaml
    automationStepPolicyFeature: {
      maxInstances: 1,
    },
    // @ts-expect-error Default Policy Value from free_monthly_plan_v0.yaml
    automationsStudioStepsPolicyFeature: {
      maxInstances: 100,
      categoryRestrictions: [
        {
          name: 'type',
          allowedValues: ['action', 'condition', 'delay', 'trigger'],
        },
      ],
    },
    // @ts-expect-error Default Policy Value from free_monthly_plan_v0.yaml
    campaignPolicyFeature: {
      categoryRestrictions: [
        {
          name: 'type',
          allowedValues: [
            'regular',
            'plaintext',
            'rss',
            'trans',
            'auto',
            'automation',
            'reconfirm',
            'tx',
          ],
        },
      ],
    },
    // @ts-expect-error Default Policy Value from free_monthly_plan_v0.yaml
    transactionFeePolicyFeature: {
      tier: 1,
    },
    // @ts-expect-error Default Policy Value from scheduling_addon_free.yaml
    appointmentServicesPolicyFeature: {
      maxInstances: 1,
    },
  },
  timezoneLabel: '',
  timezoneOffset: 0,
  timezoneId: '',
  hasLegacyPaidPlan: false,
  isAgencyPartner: false,
  hasPartnerCenterAccess: false,
  isPartnerLoggedIntoClientAccount: false,
  // @ts-expect-error ID zero values = null
  clientAccountAgencyUid: null,
  canSeeMandrill: false,
  hasCompositeAppAccess: false,
  isMcAdminUser: false,
  hasMonthlyPlan: false,
  shard: '',
  hasNeaSite: false,
  accountCreatedDate: 0,
  currencyCode: '',
  hasStore: false,
  hasAudience: false,
  hasConnectedEcomm: false,
  hasClassicAutomations: false,
  hasCustomerJourneys: false,
  hasGAIntegration: false,
  prefersNuni: false,
  hasNoEditorPreference: false,
  whyNotSend: '',
  whyNotSendTitle: '',
  startOfWeek: '',
  hasProvisionalAccount: false,
  intuitProvisionalAccountId: '',
  hasQboAccountAndLoginLink: false,
  intuitQboRealmId: '',
  industry: '',
  businessName: '',
  launchOicu: false,
  hasAdvancedSegmentation: false,
  whatsappAppId: '',
  // @ts-expect-error ID zero values = null
  solutionId: null,
  hasProducts: false,
  canAccessContentGen: false,
  // @ts-expect-error Default value for contentGenEcommData
  contentGenEcommData: null,
  isBilledMonthly: false,
  isUserInSmsSupportedCountry: false,
  isUserInPopupFormsSupportedCountry: false,
  isCr2: false,
  isNonHighValueCr2User: false,
  isHighValue: false,
  smsPlan: {
    monthlyCredits: 0,
    nextBillingDate: '',
    price: 0,
    isCancelled: false,
    cancelledWithinCurrentCycle: false,
    renewalCredits: 0,
    renewalCreditsPrice: 0,
    cancelledAt: '',
    isBillingFailed: false,
    hasFreeTrialDiscount: false,
    isEligibleForGlobalSmsTrials: false,
    nextBillingDateAfterFreeTrial: '',
  },

  requiresApprovalForLau: false,
  // @ts-expect-error ID zero values = null
  currentlySelectedAudienceId: null,
  shouldShowPubToLegacy: false,
  isTritonChatEnabled: false,
  isEcommerceConnectedUser: false,
  hasACPEligiblePlan: false,
  billing: {
    isRenewalFailed: false,
    isInPaymentGracePeriod: false,
    promptToAddPaymentMethod: false,
    nextBillingDate: '',
  },
  paginationPageSize: 0,
  elcIsEnabled: false,
  // @ts-expect-error Default value for genAiTrialInfo
  genAiTrialInfo: null,
  isInternal: false,
  username: '',
  hasSmsEnabled: false,
  hasAcceptedContentGenTerms: false,
  datePatternPreference: '',
  // @ts-expect-error Default value for pausedUserData
  pausedUserData: null,
  promotionMessaging: null,
  locale: '',
};

const appConfigZeroValues: GetAppConfigResponse = {
  appVersion: '',
  phpVersion: '',
  mediaBuildVersion: '',
  kbLocales: {
    en: '',
    es: '',
    fr: '',
    ptBr: '',
    de: '',
    it: '',
  },
  autolyseUrlHost: '',
  supportBlockOptions: {
    shouldShowLiveExpertHelpButton: false,
    canViewEmail: false,
    canViewChat: false,
    isChatAvailable: false,
    canViewTalk: false,
    isPFree: false,
    pFreeDaysLeft: 0,
    zdRoadblock: {
      isVisible: '',
      bodyContent: '',
    },
  },
  supportL10nData: {
    locale: '',
    localizedContent: {},
  },
  interop: {
    rootUrl: '',
    proxyBaseUrl: '',
    listManageDomain: '',
    pusherKey: '',
    cdnImagesDomain: '',
    galleryDomain: '',
    avestaEnvironment: '',
    solvvyUrl: '',
    msaasEnv: '',
  },
  csrfToken: '',
  bugsnag: {
    key: '',
    user: {
      id: 0,
      name: '',
      role: '',
      type: '',
    },
  },
  googleMaps: {
    endpoint: '',
    keys: {
      automations: '',
      places: '',
      segmentationMaps: '',
    },
  },
  globalLinks: [],
  crmLinks: [],
  shouldShowMainNavigation: false,
  pusher: {
    key: '',
    bulkArchiveChannel: '',
    connectedSitesChannel: '',
  },
  reauth: {
    url: '',
    rememberMe: false,
  },
  imageProxy: {
    proxyHost: '',
    studioHosts: [],
    supportedExtensions: [],
  },
  loginDomain: '',
  adaUserMetafields: {
    locale: '',
    referral: '',
    userId: '',
    firstName: '',
    lastName: '',
    loginId: 0,
    loginEmail: '',
    userLevel: '',
    packages: [],
    unixActivated: 0,
    dc: '',
    ls: 0,
    hv: false,
    cc: '',
    url: '',
    editor: '',
    userIdNoPrefix: 0,
    commercePackage: '',
    mrr: 0,
    companyName: '',
    phoneNumber: '',
    workflow: '',
    eCommerce: '',
    premiumConversionDate: '',
    csOnboardingJourneyStartDate: '',
    csOnboardingJourneyEndDate: '',
    highValue: '',
    launchOicu: false,
    launchTriton: false,
    hasRightRailFlag: false,
    rightRailHighValueEligible: false,
    rightRailGeneralCohortEligible: false,
    multiAccount: '',
    quickbooksProadvisorSupport: false,
    commerceUserLevel: '',
    supportKnobKey: '',
    partnerLevel: '',
    pfreeExpiry: 0,
  },
  xapi: {
    env: '',
  },
  idx: {
    environment: '',
  },
  mcPromptMgr: {
    imageGenUrl: '',
  },
  adaHandle: '',
  appFabricConfig: {
    appId: '',
    isEnabled: false,
    endpoint: '',
    locale: '',
    iceUrl: '',
    version: '',
  },
};

const flagsDataZeroValues: GetFlagsResponse = {
  flags: {},
};

const analyticsZeroValues: GetAnalyticsDataResponse = {
  // @ts-expect-error ID zero values = null
  mcUserId: null,
  // @ts-expect-error ID zero values = null
  mcLoginId: null,
  // @ts-expect-error ID zero values = null
  intuitCompanyId: null,
  // @ts-expect-error ID zero values = null
  intuitPseudonymId: null,
  segmentWriteKey: '',
  isInternal: false,
  isDebugModeEnabled: false,
  langPreference: '',
  ecsLibSrc: '',
  langPage: '',
  branchIoKey: '',
  hasUser: false,
  hasAgent: false,
  isFullstoryEnabled: false,
  // @ts-expect-error ID zero values = null
  mcadminAgentId: null,
  flags: {},
  cookieConfig: {
    cookiePreferencesScriptCountryCode: '',
    cookiePreferencesScriptRegionCode: '',
    cookiePreferencesPath: '',
    cookiePreferencesLegalDomainScriptId: '',
  },
  glanceConfig: { scriptSrc: '', groupId: '' },
  windowPropertiesConfig: {
    bugsnagApiKey: '',
    zendeskLiveChatKey: '',
    bugsnagAppfabricApiKey: '',
  },
  googleAnalyticsConfig: {
    hasUser: false,
    userId: 0,
    loginId: '',
    cohort: '',
    ua: '',
    version: '',
    gaDimensions: {},
    deferToGtm: false,
    suggestionsPanelEnabled: false,
    listSize: 0,
    plan: '',
    commercePlan: '',
    accountAge: 0,
    isExternalUser: false,
    lang: '',
    lastPayment: 0,
    loginIdPlain: '',
    language: '',
    visitorType: '',
    contactsSize: 0,
    userLevel: '',
    contactEmail: '',
  },
};

const pageDataZeroValues: GetPageDataResponse = {
  userData: userDataZeroValues,
  appConfig: appConfigZeroValues,
  flagsData: flagsDataZeroValues,
  analyticsData: analyticsZeroValues,
};

export default pageDataZeroValues;
export {
  userDataZeroValues,
  appConfigZeroValues,
  internalToolsZeroValues,
  analyticsZeroValues,
};
